import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { DAYS, DESCRIPTION, TERM } from "../consts";
import { routes } from "@/routes/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { formValues, schema } from "./validations";
import { useAddTaskTermMutation } from "@/services/masterData";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import useUpdateTaskTermMutation from "@/services/masterData/taskterm/useUpdateTaskTermMutation";
import useGetTaskTermDetailQuery from "@/services/masterData/taskterm/useTaskTermDetailMutation";

const useAddEdit = () => {
  const { action, id } = useParams();
  const navigate = useNavigate();
  const isView = action == routes.view;
  const isEdit = action == routes.edit;
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  const { mutateAsync: addTaskTerm, isPending } = useAddTaskTermMutation({
    onSuccess: () => {
      toast.success("Task term created successfully");
      onBackClick();
    },
    onError: (error: any) => {
      const errorCode = error?.response?.data?.issues?.errorCode;
      const backendMessage =
        errorCode === "DUPLICATE_TASK_TERM"
          ? "Task Term name already exists. Please use a unique name."
          : error?.response?.data?.issues?.message ||
            error?.response?.data?.message ||
            "Failed to create task term";

      toast.error(backendMessage);
    },
  });

  const { data: detailRes, isPending: isDetailLoading } =
    useGetTaskTermDetailQuery(isEdit || isView ? String(id) : undefined);

  const { mutateAsync: updateTaskTerm, isPending: isUpdating } =
    useUpdateTaskTermMutation({
      onSuccess: () => {
        toast.success("Task Term updated successfully");
        onBackClick();
      },
      onError: (error: any) => {
        toast.error(
          error.response.data?.issues?.message ||
            error.response.data.message ||
            "Failed to update task term"
        );
      },
    });

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [TERM]: "",
      [DAYS]: "",
      [DESCRIPTION]: "",
    },
  });

  const requiredFields = getRequiredFields(schema);

  useEffect(() => {
    if ((isEdit || isView) && detailRes?.data) {
      const d = detailRes.data;
      form.reset({
        [TERM]: String(d?.taskTerm ?? ""),
        [DAYS]: String(d?.days ?? ""),
        [DESCRIPTION]: d?.taskText ? String(d?.taskText) : "",
      });
    }
  }, [detailRes, isEdit, isView, form]);

  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.masterData}`);
  };

  const onSubmit = async (data: formValues) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    const payload = {
      taskTerm: String(_data[TERM] ?? ""),
      days: _data[DAYS] ? Number(_data[DAYS]) : undefined,
      taskText: _data[DESCRIPTION] ? String(_data[DESCRIPTION]) : undefined,
    };
    if (isEdit && id) {
      await updateTaskTerm({ id: String(id), data: payload });
    } else {
      await addTaskTerm({ data: payload });
    }
    queryClient.invalidateQueries({ queryKey: ["taskTerm"] });
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel: onBackClick,
    isMobile,
    action,
    isEdit,
    isLoading: isPending,
  };
};

export default useAddEdit;
